// The QTag step's one drawing (TASK-153.5, redrawn scan-first by TASK-177.3,
// decision-41).
//
// Rendered by the capture wizard AND the builder preview from this single
// module — two drawings would be two places for the step to drift apart,
// which is exactly how the site-select step's preview went missing (doc-6).
//
// SCAN-FIRST. There are no mode tabs any more. One scan box, and the TAG
// decides the branch — the server decides which, and says so in the lookup's
// `capture.branch` (Qtags::CaptureBranch), because it is the same decision the
// submit makes and a client that guessed would open a pane the submit refuses.
//
// The module renders; the caller wires. `state` is the step's whole memory:
//   phase          "entry" | "fresh" | "untagged" | "resolved" | "foreign"
//   payload        the lookup answer (the MemberQtagLookupResponse shape, plus
//                  the web-only `capture` key)
//   reference      what was typed, as entered
//   productTypeId  the product chosen on the fresh / untagged branch
//   pin            {page_number, x_percent, y_percent, elevation_level} | null
//                  — what to SHOW. What gets WRITTEN is the wizard's business.
//   pinContext     {available, elevationLabel, url} — whether the Where step
//                  has yet named a floor with a plan, and where its picker is
//   error          the message for the error line, or null
//
// `interactive: false` (the preview) draws the entry state with controls
// disabled; the caller binds nothing.

function esc(value) {
  const div = document.createElement("div")
  div.textContent = value == null ? "" : String(value)
  return div.innerHTML
}

function chainLine(node) {
  if (!node) return ""
  const parts = [node.block?.name, node.elevation?.name, node.location?.name].filter(Boolean)
  return parts.join(" · ")
}

export function assetSelectTitle() {
  return "QTag"
}

// Placement (ids and all — it is the caller's own tree) → the tagged
// `_site_select` shape the wizard writes, mirroring the mobile seed
// (site_select_seed.dart): a level not answered is ABSENT, never `{}`.
export function siteSelectSeedFromPlacement(placement) {
  if (!placement?.site?.id) return null

  const seed = { action: "existing", site_id: placement.site.id }
  if (placement.block?.id) seed.block = { id: placement.block.id }
  if (placement.elevation?.id) seed.elevation = { id: placement.elevation.id }
  if (placement.location?.id) seed.location = { id: placement.location.id }
  return seed
}

function errorLine(state) {
  return `<p class="text-sm text-red-600 ${state.error ? "" : "hidden"}" data-asset-select-error>${esc(state.error || "")}</p>`
}

// THE PIN, ON THE ASSET (decision-41). No policy switch decides whether this
// appears — it is offered whenever the floor the Where step names has a plan,
// and says so plainly when it does not. The step is step 0 and Where comes
// after, so "not yet" is an ordinary state rather than a fault: with free hub
// navigation the engineer answers Where and comes back, and the linked-tag
// branch seeds Where from the placement so it is usually already answered.
function pinBlock(state, interactive) {
  const heading = `<span class="block text-sm font-medium text-gray-700 mb-1">Pin on floorplan <span class="font-normal text-gray-500">(optional)</span></span>`
  const context = state.pinContext || {}

  if (!context.available) {
    return `
      <div data-asset-select-pin>
        ${heading}
        <div class="rounded border border-dashed border-gray-300 px-3 py-2.5 text-sm text-gray-500">
          Pick a floor on the Where step first — pinning uses that floor's plan.
        </div>
      </div>
    `
  }

  const pin = state.pin
  const level = pin && (pin.elevation_level ?? context.elevationLevel)
  const openAttrs = interactive ? `
    data-action="click->modal#open:prevent"
    data-modal-turbo-frame-src-value="${esc(context.url)}"
    data-modal-size-value="modal-4xl"
    data-modal-title-value="Pin the Asset on the Floor Plan"
    data-modal-title-verbatim-value="true"` : "disabled"

  if (!pin) {
    return `
      <div data-asset-select-pin>
        ${heading}
        <button type="button" class="btn-outline-gray-sm" ${openAttrs} data-asset-select-pin-open>
          Pin on floorplan
        </button>
        <span class="block text-xs text-gray-500 mt-1">${esc(context.elevationLabel || "This floor")} has a plan attached.</span>
      </div>
    `
  }

  return `
    <div data-asset-select-pin>
      ${heading}
      <div class="bg-white border border-gray-300 rounded-lg px-3 py-2.5 flex items-center gap-3">
        <span class="text-sm text-gray-900" data-asset-select-pin-summary>Level ${esc(level)} · page ${esc(pin.page_number)}</span>
        <span class="ml-auto flex items-center gap-3">
          <button type="button" class="text-sm font-medium text-blue-600" ${openAttrs} data-asset-select-pin-open>Move pin</button>
          <button type="button" class="text-sm font-medium text-gray-500" ${interactive ? "" : "disabled"} data-asset-select-pin-remove>Remove</button>
        </span>
      </div>
    </div>
  `
}

// "What is it?" — the new asset the tag is about to go on, or (with the step
// optional) the one being recorded without a tag at all.
function newAssetPane(state, interactive) {
  const products = state.products || []
  const chosen = state.productTypeId || ""

  if (!products.length) {
    return `
      <p class="text-sm text-gray-500">No products are set up for this project yet — ask an admin to add some
      under the project's Products step.</p>
    `
  }

  const options = products.map(p => `
    <option value="${esc(p.id)}" ${p.id === chosen ? "selected" : ""}>${
      esc([ p.name, p.sku ].filter(Boolean).join(" · "))
    }</option>`).join("")

  return `
    <label class="block">
      <span class="block text-sm font-medium text-gray-700 mb-1">What is it?</span>
      <select class="form-control w-full" ${interactive ? "" : "disabled"} data-asset-select-product>
        <option value="">Choose a product…</option>
        ${options}
      </select>
    </label>
    ${pinBlock(state, interactive)}
  `
}

export function renderAssetSelectStep(container, { config = {}, state = {}, interactive = true } = {}) {
  // The RESOLVED wire shape is still the legacy one (decision-41's
  // compatibility spine), so it is read here in the retired vocabulary and
  // meant in the new one: inspect === "required" IS qtag_step required, and
  // install === "on" IS "this project has products to record".
  const required = config.inspect === "required"
  const productsAvailable = config.install === "on" && (state.products || []).length > 0
  const phase = state.phase || "entry"

  if (phase === "fresh" && state.payload) {
    const reference = state.payload.qtag?.reference || state.reference

    container.innerHTML = `
      <div class="space-y-4" data-asset-select-step>
        <div class="flex items-start gap-2.5 bg-green-50 border border-green-300 rounded-md p-3" data-asset-select-fresh>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" class="h-5 w-5 shrink-0" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#16a34a" stroke-width="1.7"/><path d="M8.5 12.5l2.4 2.4 4.8-5.4" stroke="#16a34a" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <p class="text-sm leading-snug text-green-800"><span class="font-semibold">${esc(reference)}</span> is one of your unused tags — record the asset it's going on.</p>
        </div>
        ${newAssetPane(state, interactive)}
        ${errorLine(state)}
        <div><button type="button" class="text-sm font-medium text-blue-600" data-asset-select-reset>Not this tag? Look up another</button></div>
      </div>
    `
    return
  }

  if (phase === "untagged") {
    container.innerHTML = `
      <div class="space-y-4" data-asset-select-step>
        <p class="text-sm text-gray-500" data-asset-select-untagged>Recording this asset without a tag. It goes on the record where the Where step says.</p>
        ${newAssetPane(state, interactive)}
        ${errorLine(state)}
        <div><button type="button" class="text-sm font-medium text-blue-600" data-asset-select-reset>Scan a tag instead</button></div>
      </div>
    `
    return
  }

  if (phase === "resolved" && state.payload) {
    const payload = state.payload
    const placement = payload.linked?.placement
    const resolvedFrom = payload.resolved_from
    const banner = resolvedFrom ? `
      <div class="rounded-lg bg-blue-100 px-4 py-3 text-sm text-blue-900">
        You scanned <span class="font-semibold">${esc(resolvedFrom.reference)}</span> — that label was
        replaced; this is now <span class="font-semibold">${esc(payload.qtag?.reference)}</span>.
      </div>` : ""

    container.innerHTML = `
      <div class="space-y-4" data-asset-select-step>
        ${banner}
        <div class="bg-white border border-gray-300 rounded-lg p-4 space-y-2">
          <div class="flex items-center gap-2">
            <span class="text-green-600" aria-hidden="true">✓</span>
            <span class="font-medium text-gray-900">In your ${esc(placement?.site?.name || "sites")}</span>
            <span class="ml-auto inline-flex items-center rounded-full border border-blue-600 px-2.5 py-0.5 text-xs text-blue-700">${esc(payload.qtag?.reference)}</span>
          </div>
          ${chainLine(placement) ? `<div class="text-sm text-gray-600">${esc(chainLine(placement))}</div>` : ""}
          ${payload.linked?.product_type?.name ? `<div class="text-sm text-gray-600">${esc(payload.linked.product_type.name)}</div>` : ""}
        </div>
        <p class="text-xs text-gray-500">This submission logs against this asset. The Where step is filled in from this placement — you can still change it there.</p>
        ${pinBlock(state, interactive)}
        ${errorLine(state)}
        <button type="button" class="text-sm font-medium text-blue-600" data-asset-select-reset>Not this tag? Look up another</button>
      </div>
    `
    return
  }

  if (phase === "foreign" && state.payload) {
    const payload = state.payload
    const context = payload.linked?.placement_context

    container.innerHTML = `
      <div class="space-y-4" data-asset-select-step>
        <div class="rounded-lg bg-blue-100 px-4 py-3 space-y-1">
          <div class="text-xs font-semibold tracking-wide text-blue-800 uppercase">As ${esc(context?.team?.name || "another team")} records it</div>
          <div class="text-sm text-blue-900">${esc([context?.site?.name, chainLine(context)].filter(Boolean).join(" · "))}</div>
          ${payload.linked?.product_type?.name ? `<div class="text-sm text-blue-900">${esc(payload.linked.product_type.name)}</div>` : ""}
        </div>
        <p class="text-sm text-gray-500">This tag isn't in your sites yet. Adding it copies this branch into your account — nothing links back to theirs.</p>
        <button type="button" class="btn-blue" data-asset-select-add>Add to my sites</button>
        ${errorLine(state)}
        <div><button type="button" class="text-sm font-medium text-blue-600" data-asset-select-reset>Look up a different tag</button></div>
      </div>
    `
    return
  }

  // ENTRY. One box, and the only escape from it: recording an asset with no
  // tag at all, offered ONLY when the project allows a submission without one
  // (Required IS the tag mandate) and only when there are products to record.
  const noTagLink = !required && productsAvailable ? `
    <div>
      <button type="button" class="text-sm font-medium text-blue-600" ${interactive ? "" : "disabled"}
              data-asset-select-untag>No tag? Record the asset without one</button>
    </div>
  ` : ""

  container.innerHTML = `
    <div class="space-y-4" data-asset-select-step>
      <p class="text-sm text-gray-500">Scan or type the reference on the tag — the thing this submission is about.${required ? "" : " You can skip this step."}</p>
      <div class="flex gap-2">
        <input type="text" class="form-control flex-1" placeholder="A000123"
               value="${esc(state.reference || "")}" ${interactive ? "" : "disabled"}
               data-asset-select-input autocapitalize="characters" autocomplete="off">
        <button type="button" class="btn-blue shrink-0" ${interactive ? "" : "disabled"}
                data-asset-select-lookup>Look it up</button>
      </div>
      ${errorLine(state)}
      ${noTagLink}
    </div>
  `
}
